/**
 * PetMultiPicker — tick the customer's pets that are coming, or add one.
 *
 * Lists every pet on the customer's record as a toggle card (species + a
 * one-line vaccine summary) and offers an inline "Add a pet" form. Replaces
 * the old "one dropdown per pet slot" flow, which needed a click per slot
 * just to reveal the same list again.
 */
import { createPet, listPetsByCustomer } from '@/lib/api';
import type { Pet, PetWithVaccinations } from '@/lib/types';
import { cn } from '@/lib/utils';
import { Cat, Check, Dog, Loader2, Plus, X } from 'lucide-react';
import { useEffect, useState } from 'react';

interface Props {
  customerId: string | null;
  selectedIds: string[];
  onToggle: (pet: PetWithVaccinations) => void;
  /** Only one pet may be selected (grooming appointments). */
  single?: boolean;
}

interface NewPetForm {
  name: string;
  species: 'dog' | 'cat';
  breed: string;
}

const emptyForm = (): NewPetForm => ({ name: '', species: 'dog', breed: '' });

export function PetMultiPicker({ customerId, selectedIds, onToggle, single = false }: Props) {
  const [pets, setPets] = useState<PetWithVaccinations[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<NewPetForm>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (!customerId) {
      setPets([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    listPetsByCustomer(customerId)
      .then((rows) => {
        if (!cancelled) setPets(rows);
      })
      .catch(() => {
        if (!cancelled) setPets([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  const save = async () => {
    if (!customerId || !form.name.trim()) return;
    setSaving(true);
    setSaveError('');
    try {
      const created: Pet = await createPet({
        customerId,
        name: form.name.trim(),
        species: form.species,
        breed: form.breed.trim() || null,
      });
      const withVaccinations: PetWithVaccinations = { ...created, vaccinations: [] };
      setPets((prev) => [...prev, withVaccinations]);
      onToggle(withVaccinations);
      setShowCreate(false);
      setForm(emptyForm());
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to add pet');
    } finally {
      setSaving(false);
    }
  };

  if (!customerId) {
    return (
      <div className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-muted-foreground">
        Select a customer first
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />
          Loading pets…
        </div>
      )}
      {!loading && pets.length === 0 && !showCreate && (
        <p className="text-sm text-muted-foreground">No pets on file yet — add one below.</p>
      )}
      {!loading && pets.length > 0 && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {pets.map((p) => {
            const selected = selectedIds.includes(p.id);
            const invalid = p.vaccinations.filter((v) => !v.valid).length;
            const Icon = p.species === 'cat' ? Cat : Dog;
            return (
              <button
                key={p.id}
                type="button"
                role="checkbox"
                aria-checked={selected}
                onClick={() => onToggle(p)}
                className={cn(
                  'flex items-center gap-3 rounded-xl border p-3 text-left transition-colors press',
                  selected
                    ? 'border-primary bg-primary/10'
                    : 'border-border bg-background hover:border-primary/40',
                )}
              >
                <span
                  className={cn(
                    'grid size-5 shrink-0 place-items-center rounded-md ring-1',
                    selected
                      ? 'bg-primary text-primary-foreground ring-primary'
                      : 'bg-surface ring-border',
                  )}
                >
                  {selected && <Check className="size-3.5" strokeWidth={3} />}
                </span>
                <Icon className="size-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{p.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {[p.breed, p.sizeCategory].filter(Boolean).join(' · ') || p.species}
                  </span>
                </span>
                {p.vaccinations.length === 0 ? (
                  <span className="shrink-0 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                    No vaccines
                  </span>
                ) : invalid > 0 ? (
                  <span className="shrink-0 rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-medium text-destructive">
                    {invalid} expired
                  </span>
                ) : (
                  <span className="shrink-0 rounded-full bg-success/10 px-2 py-0.5 text-[10px] font-medium text-success">
                    Vaccines ok
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
      {single && selectedIds.length > 1 && (
        <p className="text-xs text-destructive">
          Grooming appointments are booked one pet at a time — keep one pet ticked.
        </p>
      )}

      {showCreate ? (
        <form
          className="space-y-2 rounded-lg border border-border bg-surface p-3"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
          aria-label="New pet"
        >
          <div className="flex items-center justify-between">
            <p className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              New pet
            </p>
            <button
              type="button"
              onClick={() => {
                setShowCreate(false);
                setForm(emptyForm());
              }}
              className="grid size-6 place-items-center rounded-md text-muted-foreground hover:bg-secondary press"
              aria-label="Cancel new pet"
            >
              <X className="size-3.5" strokeWidth={1.75} />
            </button>
          </div>
          <input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Pet name"
            aria-label="Pet name"
            autoFocus
            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring/30"
          />
          <div className="flex gap-2">
            {(['dog', 'cat'] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setForm((f) => ({ ...f, species: s }))}
                className={cn(
                  'flex-1 rounded-md border px-2 py-1.5 text-xs font-medium press',
                  form.species === s
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:bg-secondary',
                )}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
          <input
            value={form.breed}
            onChange={(e) => setForm((f) => ({ ...f, breed: e.target.value }))}
            placeholder="Breed (optional)"
            aria-label="Breed"
            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring/30"
          />
          {saveError && <p className="text-xs text-destructive">{saveError}</p>}
          <button
            type="submit"
            disabled={!form.name.trim() || saving}
            className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground press disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Add pet'}
          </button>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 text-sm text-primary press"
        >
          <Plus className="size-4" strokeWidth={2} />
          Add a pet
        </button>
      )}
    </div>
  );
}
